import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useAppDispatch } from '@store/hooks';
import { alertMessage } from '@slices/alertSlice';
import { useLazyQuery } from '@apollo/client';
import { GET_USER_PLAYER_REPORT } from '@api/ending';
import {
  ReachedEnding,
  EndingRateRangeLevelType,
  Ending,
  UserHasEnding,
} from '@customTypes/story';
import { collectibleEnding } from '@customTypes/chapter';
import { getRarityText, getStringToDate } from '@common/utils';
import theme from '@styles/theme';
import {
  PyramidIcon4,
  PyramidIcon3,
  PyramidIcon1,
  PyramidIcon2,
  ClosePopupBtn,
  PyramidStoryGameIcon1,
  PyramidStoryGameIcon4,
  PyramidStoryGameIcon2,
  PyramidStoryGameIcon3,
} from 'public/assets';
import { css } from '@emotion/react';
import { MBTI19_URL } from '@common/values';

import { Button, SPImage, SPLoading } from '@components/commons';
import PlayerReportIframe from './PlayerReportIframe';

export interface IEndingCard {
  ending: ReachedEnding | collectibleEnding;
  onClose?: () => void;
  isShowButton?: boolean;
  isShowReport?: boolean;
  isPopup?: boolean;
}

export const getRangeLevelIcon = ({
  rateLevel,
  isStoryGame,
}: {
  rateLevel?: EndingRateRangeLevelType;
  isStoryGame?: boolean;
}) => {
  switch (rateLevel) {
    case 'Normal':
      return isStoryGame ? (
        <PyramidStoryGameIcon4 />
      ) : (
        <PyramidIcon4 width={16} height={16} />
      );
    case 'Rarity':
      return isStoryGame ? (
        <PyramidStoryGameIcon3 />
      ) : (
        <PyramidIcon3 width={16} height={16} />
      );
    case 'VeryRarity':
      return isStoryGame ? (
        <PyramidStoryGameIcon2 />
      ) : (
        <PyramidIcon2 width={16} height={16} />
      );
    case 'Minority':
      return isStoryGame ? (
        <PyramidStoryGameIcon1 />
      ) : (
        <PyramidIcon1 width={16} height={16} />
      );
    default:
      return isStoryGame ? (
        <PyramidStoryGameIcon4 />
      ) : (
        <PyramidIcon4 width={16} height={16} />
      );
  }
};

const EndingCard = ({
  ending,
  onClose,
  isShowButton = false,
  isShowReport = false,
  isPopup = false,
}: IEndingCard) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [endingInfo, setEndingInfo] = useState<Ending>(ending?.ending);
  const [collectedEnding, setCollectedEnding] = useState<
    UserHasEnding | undefined
  >(undefined);
  const [reportUrl, setReportUrl] = useState<string>();

  const [getPlayerReport, { loading: playerReportLoading }] = useLazyQuery<{
    getUserPlayerReport: {
      url?: string;
      storyId: number;
      storyName: string;
      reportId: number;
      endingId: number;
    };
  }>(GET_USER_PLAYER_REPORT, {
    onCompleted: (res) => {
      const getUserPlayerReport = res?.getUserPlayerReport;
      getUserPlayerReport?.url &&
        setReportUrl(getUserPlayerReport?.url + '?excludeExtra=0');
    },
    onError: (error) => {
      dispatch(
        alertMessage({
          message: t('common_popup_description_error_occurred'),
          type: 'warning',
        }),
      );
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    isShowReport && onShowReport();
  }, [isShowReport]);

  useEffect(() => {
    const collectedEnding =
      'collectedEnding' in ending
        ? ending?.collectedEnding
        : 'userHasEnding' in ending
        ? ending?.userHasEnding
        : undefined;
    setCollectedEnding(collectedEnding);
    setEndingInfo(ending?.ending);
  }, [ending]);

  useEffect(() => {
    if (!isPopup) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const onShowReport = () => {
    collectedEnding?.uheId &&
      getPlayerReport({
        variables: { id: collectedEnding?.uheId },
      });
  };

  const onClosePopup = () => {
    reportUrl ? setReportUrl(undefined) : onClose?.();
  };

  return (
    <>
      <div
        css={popupContainer({
          isPopup,
        })}
      >
        {endingInfo &&
          (reportUrl ? (
            <PlayerReportIframe
              src={reportUrl}
              onClose={() => setReportUrl(undefined)}
            />
          ) : (
            <div
              className={`container ${
                'userHasEnding' in ending ? 'fixed' : undefined
              }`}
            >
              {isPopup && (
                <div className="close">
                  <div className="report-container">
                    {collectedEnding?.hasPlayerReport && (
                      <>
                        <div className="report" onClick={onShowReport}>
                          {t('chapterending_screen_button_show_player_report')}
                          {collectedEnding?.hasNewPlayerReport && (
                            <div className="new-button">N</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="report-close">
                    <ClosePopupBtn
                      width={40}
                      height={40}
                      onClick={onClosePopup}
                    />
                  </div>
                </div>
              )}
              <div className="image-container">
                {endingInfo?.imageFile?.link && (
                  <SPImage
                    className="image"
                    src={endingInfo?.imageFile?.link}
                    layout="fill"
                    objectFit="cover"
                    alt="Ending image"
                  />
                )}
              </div>
              <div className="content">
                <div className="title">{endingInfo.story?.name}</div>
                <div className="ending-title">
                  {collectedEnding?.customName ?? endingInfo.name}
                </div>
                <div className="content-text">
                  {collectedEnding?.customDescription ??
                    endingInfo.collectionDescription}
                </div>
                {endingInfo?.arrivalRate && endingInfo.rateLevel && (
                  <div className="rate">
                    <div className="rate-content">
                      {getRangeLevelIcon({
                        rateLevel: endingInfo.rateLevel,
                      })}
                      <div className="rate-text">
                        {endingInfo?.arrivalRate ?? '-'}%
                      </div>
                      <div className="point"></div>
                      <div className="rate-type">
                        {getRarityText(t, endingInfo?.rateLevel)}
                      </div>
                    </div>
                    <div className="date">
                      {getStringToDate(collectedEnding?.updatedAt)}
                    </div>
                  </div>
                )}
              </div>
              {isShowButton && (
                <Button
                  title={t(
                    'chapterending_screen_button_try_adult_spicy_version',
                  )}
                  customCSS={buttonStyle}
                  isFill={false}
                  onClick={() => router.push(MBTI19_URL)}
                />
              )}
            </div>
          ))}
        {playerReportLoading && <SPLoading />}
      </div>
    </>
  );
};

const buttonStyle = css`
  margin-top: 1rem;
  width: 100%;
  height: 3rem;
  font-weight: 700;
  font-size: 1rem;
  color: ${theme.colors.primary500};
  border: 1px solid ${theme.colors.primary300};
  background-color: ${theme.colors.white};
  border-radius: 0.5rem;
  cursor: pointer;
`;

const popupContainer = ({ isPopup }: { isPopup: boolean }) => css`
  position: ${isPopup ? 'absolute' : 'relative'};
  width: 100%;
  max-width: ${isPopup ? '50rem' : '100%'};
  display: flex;
  flex-direction: column;
  align-items: center;

  .container {
    position: relative;
    padding: ${isPopup ? '0 1.25rem' : '0'};
    width: 100%;
  }

  .container.fixed {
    width: 100%;
    max-width: ${theme.maxWidth};
  }

  .close {
    position: absolute;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    top: -3.5rem;
    right: 0;
    z-index: 1;
    padding: 0 1.25rem;

    .report-container {
      cursor: pointer;
    }

    .report-close {
      cursor: pointer;
    }
  }

  .content {
    width: 100%;
    background-color: ${theme.colors.white};
    border-radius: 0 0 0.75rem 0.75rem;
    padding: 1.5rem;
    background-color: ${theme.colors.gray900};
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .image {
    border-radius: 0.75rem 0.75rem 0 0;
  }

  .image-container {
    width: 100%;
    height: ${isPopup ? '20rem' : '17.0625rem'};
    background-color: ${theme.colors.gray100};
    position: relative;
    border-radius: 0.75rem 0.75rem 0 0;
    object-fit: cover
      ${isPopup &&
      `
    @media screen and (max-width: ${theme.devices.mobile}) {
      height: 9rem;
    }
    `};
  }

  .title {
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: normal;
    font-size: 0.875rem;
    line-height: 1.25rem;
    letter-spacing: -0.3px;
    color: ${theme.colors.primary300};
  }

  .ending-title {
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: bold;
    font-size: 1.25rem;
    line-height: 1.75rem;
    letter-spacing: -0.3px;
    color: ${theme.colors.white};
  }

  .content-text {
    max-height: 340px;
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: normal;
    font-size: 0.875rem;
    line-height: 1.25rem;
    letter-spacing: -0.3px;
    color: ${theme.colors.gray500};
    overflow-y: scroll;

    @media screen and (max-width: ${theme.devices.mobile}) {
      max-height: 12.5rem;
    }
  }

  .content-text::-webkit-scrollbar {
    width: 0.625rem; /* 스크롤바의 너비 */
  }

  .rate {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .rate-content {
    display: flex;
    align-items: center;
  }

  .rate-text {
    margin-left: 0.25rem;
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: bold;
    font-size: 0.875rem;
    line-height: 1.25rem;
    letter-spacing: -0.3px;
    color: ${theme.colors.emerald500};
  }

  .point {
    width: 0.25rem;
    height: 0.25rem;
    border-radius: 0.125rem;
    background-color: ${theme.colors.gray700};
    margin: 0 0.5rem;
  }

  .rate-type {
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: normal;
    font-size: 0.875rem;
    line-height: 1.25rem;
    letter-spacing: -0.3px;
    color: ${theme.colors.white};
  }

  .date {
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: normal;
    font-size: 0.875rem;
    line-height: 1.25rem;
    text-align: right;
    letter-spacing: -0.3px;
    color: ${theme.colors.gray500};
  }

  .report-container {
    display: flex;
    z-index: 1;
  }

  .report {
    display: flex;
    align-items: center;
    padding: 0.375rem 0.5rem;
    background-color: ${theme.colors.gray900};
    color: ${theme.colors.white};
    font-weight: 400;
    font-size: 1rem;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  .new-button {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.25rem 4.5px;
    border-radius: 3px;
    font-weight: 700;
    font-size: 0.8rem;
    line-height: 0.75rem;
    margin-left: 0.375rem;
    color: ${theme.colors.white};
    background-color: ${theme.colors.emerald500};
  }
`;

export default EndingCard;
